import { cbSemanticCssVars } from '@creditornot/cb-ingredients';
import React, { useMemo } from 'react';
import styled from 'styled-components';

import { useSelectionInfoContext } from 'modules/shared-menu/contexts/SelectionInfoContext';
import { OfferOverrides } from 'modules/shared-menu/types';

import { BulkAddOptionBinding } from './BulkAddOptionBinding';
import { BulkChangeAvailabilityVisibility } from './BulkChangeAvailabilityVisibility/BulkChangeAvailabilityVisibility';
import { BulkChangeCategory } from './BulkChangeCategory';
import { BulkChangeVat } from './BulkChangeVat';
import { BulkDelete } from './BulkDelete';
import { BulkEnableDisable } from './BulkEnableDisable';
import { BulkExportToGoogleSheets } from './BulkExportToGoogleSheets';
import { BulkResetOverrides } from './BulkResetOverrides';
import { BulkPriceChange } from './PriceChange';
import { useWoltSpecials } from '../../../context/WoltSpecialsContext';
import { useOverrideSettings } from '../../../hooks/useSelectionOverrideSettings';

type BulkActionsProps = {
  selectedIds: Set<string>;
};

const Root = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 16px;
  margin-inline-start: 16px;
`;

const Divider = styled.span`
  height: 36px;
  width: 1px;
  background-color: ${cbSemanticCssVars.borderPrimary};
`;

export const availableOverridesBulkActions: (keyof OfferOverrides)[] = ['enabled'];

export const BulkActions = ({ selectedIds }: BulkActionsProps) => {
  const { vlmeConfig } = useSelectionInfoContext();
  const isSharedVenue = !!vlmeConfig?.isSharedVenue;
  const { offerAttrsEnabledForOverride } = useOverrideSettings();
  const { woltSpecialsOfferIds } = useWoltSpecials();

  const hasWoltSpecialInSelectedIds = useMemo<boolean>(() => {
    return !![...selectedIds].find(id => woltSpecialsOfferIds?.has(id));
  }, [selectedIds, woltSpecialsOfferIds]);

  if (isSharedVenue) {
    return (
      <Root>
        <BulkResetOverrides selectedIds={selectedIds} />
        {offerAttrsEnabledForOverride.includes('enabled') && <BulkEnableDisable selectedIds={selectedIds} />}
      </Root>
    );
  }

  return (
    <Root>
      <BulkEnableDisable selectedIds={selectedIds} />
      <BulkChangeAvailabilityVisibility selectedIds={selectedIds} />
      <BulkPriceChange selectedIds={selectedIds} />
      <BulkAddOptionBinding selectedIds={selectedIds} />
      <BulkChangeCategory selectedIds={selectedIds} hasWoltSpecialInSelectedIds={hasWoltSpecialInSelectedIds} />
      <BulkChangeVat selectedIds={selectedIds} />
      <BulkExportToGoogleSheets selectedIds={selectedIds} hasWoltSpecialInSelectedIds={hasWoltSpecialInSelectedIds} />
      <Divider />
      <BulkDelete selectedIds={selectedIds} />
    </Root>
  );
};
